// src/component/ImportantAnnouncementList.js
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";

import { db, auth } from "../api/firebase";
import { AppThemeContext } from "./AppShell";
import PostContent from "./PreviewPostContent";

// ──────────────────────────────
// 期・セカイムラキー
// ──────────────────────────────
const PAYMENT_TERMS = [1, 2, 3, 4];
const ALLOWED_SEKAIMURA_KEY = "sekaiMura";

const normalizeTerms = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((t) => String(t).trim())
    .filter(Boolean);

const truncate = (text, max = 120) =>
  text && text.length > max ? text.slice(0, max) + "…" : text || "";

const formatDateTime = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : ts;
  return d.toLocaleString("ja-JP");
};

const formatDate = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : ts;
  return d.toLocaleDateString("ja-JP");
};

// ──────────────────────────────
// ユーザーが属している期・セカイムラを payments から作る
// ──────────────────────────────
function buildViewerTerms(userData = {}) {
  const terms = [];

  // payments 配列の paymentPhase をすべて拾う
  const payments = Array.isArray(userData.payments) ? userData.payments : [];
  const phases = payments
    .map((p) => p && p.paymentPhase)
    .filter(Boolean)
    .map((p) => String(p).trim());

  terms.push(...phases);

  // セカイムラ会員フラグがあれば追加
  if (
    userData.sekaiMuraMember ||
    userData.isSekaiMuraMember ||
    userData.isSekaiMura
  ) {
    terms.push(ALLOWED_SEKAIMURA_KEY);
  }

  return Array.from(new Set(normalizeTerms(terms)));
}

// ──────────────────────────────
// このユーザーから見て表示してよいお知らせか？
// ──────────────────────────────
function isAnnouncementVisibleForViewer(announcement, viewerTerms) {
  if (!announcement.isVisible) return false;

  const allowed = normalizeTerms(announcement.allowedTerm);

  // allowedTerm 未設定 or 空配列 → 誰にも見せない
  if (!Array.isArray(announcement.allowedTerm) || allowed.length === 0) {
    return false;
  }

  // ユーザーの期情報が取れていないときも表示しない
  if (!viewerTerms || viewerTerms.length === 0) return false;

  // どれか1つでも一致していれば表示
  return allowed.some((t) => viewerTerms.includes(t));
}

export default function ImportantAnnouncementList() {
  const theme = useContext(AppThemeContext) || {};
  const surface = theme.surfaceBg || "#FFFFFF";
  const railText = theme.railText || "rgba(40,40,60,0.9)";
  const accent = theme.railActive || "#5A35FF";

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [viewerTerms, setViewerTerms] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);

        // ① ログインユーザーの期情報を取得
        let terms = [];
        const user = auth.currentUser;
        if (user) {
          const userSnap = await getDoc(doc(db, "users", user.uid));
          if (userSnap.exists()) {
            terms = buildViewerTerms(userSnap.data());
          }
        }
        setViewerTerms(terms);

        // ② お知らせ一覧を取得（isVisible=true のものだけ）
        const q = query(
          collection(db, "announcements"),
          where("isVisible", "==", true),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setAnnouncements(list);
      } catch (e) {
        console.error("Failed to load announcements:", e);
        setAnnouncements([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // 表示対象を絞る
  const visibleAnnouncements = announcements.filter((a) =>
    isAnnouncementVisibleForViewer(a, viewerTerms)
  );

  return (
    <Box>
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Stack direction="row" spacing={1} justifyContent="center">
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: railText }}>
              読み込み中…
            </Typography>
          </Stack>
        </Box>
      ) : visibleAnnouncements.length === 0 ? (
        <Typography
          variant="body2"
          sx={{ color: "rgba(0,0,0,0.6)", mt: 1.5 }}
        >
          表示できるお知らせがありません。
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ width: "100%", maxWidth: "100%" }}>
          {visibleAnnouncements.map((a) => {
            const dateLabel = a.createdAt
              ? formatDate(a.createdAt)
              : "日付未設定";

            const teaser =
              truncate(a.content || a.body || a.text || "") ||
              "本文がまだ登録されていません。";

            return (
              <Card
                key={a.id}
                onClick={() => setSelected(a)}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.95)",
                  border: `1px solid ${accent}22`,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.06)",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: "0 14px 30px rgba(0,0,0,0.10)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all .18s ease-out",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: { xs: 1.5, sm: 2 },
                    width: "100%",
                    maxWidth: "100%",
                    p: 1.5,
                  }}
                >
                  {/* 左側：日付バッジ */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: { xs: 96, sm: 120 },
                      height: { xs: 40, sm: 44 },
                      borderRadius: 3,
                      background:
                        "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                        `linear-gradient(135deg, ${accent}22, ${accent}11)`,
                      boxShadow:
                        "0 10px 25px rgba(0,0,0,0.10), inset 0 0 0 1px rgba(255,255,255,0.9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: railText,
                        fontSize: 12,
                      }}
                    >
                      {dateLabel}
                    </Typography>
                  </Box>

                  {/* 右側：タイトル + 本文 */}
                  <Box
                    sx={{
                      flexGrow: 1,
                      minWidth: 0,
                      width: "100%",
                      maxWidth: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: railText,
                        mb: 0.4,
                        fontSize: 13,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: "vertical",
                        wordBreak: "break-word",
                      }}
                    >
                      {a.title || "（タイトル未設定）"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 12,
                        color: "rgba(0,0,0,0.65)",
                        lineHeight: 1.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        wordBreak: "break-word",
                      }}
                    >
                      {teaser}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* ▼ お知らせ内容ダイアログ */}
      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: surface,
          },
        }}
      >
        {selected && (
          <>
            <DialogTitle
              sx={{
                pb: 1,
                fontWeight: 700,
                color: railText,
              }}
            >
              {selected.title || "事務局からのお知らせ"}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color: "rgba(0,0,0,0.6)",
                }}
              >
                {selected.createdAt
                  ? formatDateTime(selected.createdAt)
                  : ""}
              </Typography>
            </DialogTitle>

            <DialogContent
              sx={{
                pt: 2,
                pb: 3,
                bgcolor: "rgba(255,255,255,0.9)",
              }}
            >
              <PostContent content={selected.content || ""} />
            </DialogContent>

            <Box sx={{ textAlign: "right", pr: 3, pb: 2 }}>
              <Button onClick={() => setSelected(null)}>閉じる</Button>
            </Box>
          </>
        )}
      </Dialog>
    </Box>
  );
}
